import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, Plus, Search, Filter, Download, FileText, 
  CheckCircle, Clock, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, Eye, Pencil, Trash2, Receipt, CreditCard, PiggyBank,
  Calendar, User, Building, ChevronDown, ChevronUp, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { facturasAPI, contactosAPI, expedientesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const ESTADOS_FACTURA = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { value: 'parcial', label: 'Pago Parcial', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  { value: 'pagada', label: 'Pagada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'vencida', label: 'Vencida', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  { value: 'anulada', label: 'Anulada', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
];

const TIPOS_FACTURA = [
  { value: 'proforma', label: 'Proforma' },
  { value: 'factura', label: 'Factura' },
  { value: 'presupuesto', label: 'Presupuesto' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const Contabilidad = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('facturas');
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFactura, setEditingFactura] = useState(null);
  
  // Contactos y Expedientes para selects
  const [contactos, setContactos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    total_pendiente: 0,
    total_pagado: 0,
    total_facturas: 0,
    facturas_vencidas: 0
  });
  
  // Form
  const [form, setForm] = useState({
    tipo: 'proforma',
    cliente_id: '',
    expediente_id: '',
    descripcion: '',
    porcentaje_iva: 21,
    porcentaje_retencion: 15,
    fecha_emision: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    forma_pago: 'TRANSFERENCIA BANCARIA',
    notas: '',
    conceptos: [{ concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [facturasRes, contactosRes, expedientesRes, statsRes] = await Promise.all([
        facturasAPI.getAll(),
        contactosAPI.getAll(),
        expedientesAPI.getAll(),
        facturasAPI.getStats()
      ]);
      
      // Manejar diferentes formatos de respuesta
      let facturasData = facturasRes.data;
      if (facturasData && facturasData.data) {
        facturasData = facturasData.data;
      }
      setFacturas(Array.isArray(facturasData) ? facturasData : []);
      
      const contactosList = contactosRes.data?.data || contactosRes.data || [];
      setContactos(Array.isArray(contactosList) ? contactosList.filter(c => c.tipo === 'cliente') : []);
      
      let expedientesData = expedientesRes.data;
      if (expedientesData && expedientesData.data) {
        expedientesData = expedientesData.data;
      }
      setExpedientes(Array.isArray(expedientesData) ? expedientesData : []);
      
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: "Error", description: "No se pudieron cargar los datos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      tipo: 'factura',
      numero: '',
      cliente_id: '',
      tipo: 'proforma',
      cliente_id: '',
      expediente_id: '',
      descripcion: '',
      porcentaje_iva: 21,
      porcentaje_retencion: 15,
      fecha_emision: new Date().toISOString().split('T')[0],
      fecha_vencimiento: '',
      forma_pago: 'TRANSFERENCIA BANCARIA',
      notas: '',
      conceptos: [{ concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }]
    });
    setEditingFactura(null);
  };

  const handleEdit = (factura) => {
    // Navegar a detalle para editar
    navigate(`/contabilidad/factura/${factura.id}`);
  };

  const addConcepto = () => {
    setForm({
      ...form,
      conceptos: [...form.conceptos, { concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }]
    });
  };

  const updateConcepto = (index, field, value) => {
    const newConceptos = [...form.conceptos];
    newConceptos[index] = { ...newConceptos[index], [field]: value };
    
    if (field === 'unidades' || field === 'precio') {
      const unidades = field === 'unidades' ? parseFloat(value) : parseFloat(newConceptos[index].unidades);
      const precio = field === 'precio' ? parseFloat(value) : parseFloat(newConceptos[index].precio);
      newConceptos[index].base_imponible = unidades * precio;
    }
    
    setForm({ ...form, conceptos: newConceptos });
  };

  const removeConcepto = (index) => {
    if (form.conceptos.length > 1) {
      setForm({ ...form, conceptos: form.conceptos.filter((_, i) => i !== index) });
    }
  };

  const calcularTotal = () => {
    const base = form.conceptos.reduce((sum, c) => sum + (c.base_imponible || 0), 0);
    const iva = base * (form.porcentaje_iva / 100);
    const retencion = base * (form.porcentaje_retencion / 100);
    return { base, iva, retencion, total: base + iva - retencion };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const totales = calcularTotal();
      const data = {
        tipo: form.tipo,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        expediente_id: form.expediente_id ? parseInt(form.expediente_id) : null,
        descripcion: form.descripcion,
        porcentaje_iva: parseFloat(form.porcentaje_iva),
        porcentaje_retencion: parseFloat(form.porcentaje_retencion),
        fecha_emision: form.fecha_emision,
        fecha_vencimiento: form.fecha_vencimiento || null,
        forma_pago: form.forma_pago,
        notas: form.notas,
        conceptos: form.conceptos.map(c => ({
          ...c,
          impuesto_pct: form.porcentaje_iva,
          retencion_pct: form.porcentaje_retencion
        })),
        suplidos: []
      };
      
      await facturasAPI.create(data);
      toast({ title: "Factura creada" });
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo guardar",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (facturaId) => {
    if (!window.confirm('¿Eliminar esta factura?')) return;
    
    try {
      await facturasAPI.delete(facturaId);
      toast({ title: "Factura eliminada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  const handleMarcarPagada = async (facturaId) => {
    try {
      await facturasAPI.marcarPagada(facturaId);
      toast({ title: "Factura marcada como pagada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Filtrar facturas - búsqueda por cualquier campo
  const filteredFacturas = facturas.filter(f => {
    const searchLower = search.toLowerCase();
    const matchSearch = !search || 
      f.numero_factura?.toLowerCase().includes(searchLower) ||
      f.cliente_nombre?.toLowerCase().includes(searchLower) ||
      f.cliente_nif?.toLowerCase().includes(searchLower) ||
      f.descripcion?.toLowerCase().includes(searchLower) ||
      f.codigo_expediente?.toLowerCase().includes(searchLower) ||
      f.notas?.toLowerCase().includes(searchLower) ||
      formatCurrency(f.total).toLowerCase().includes(searchLower) ||
      formatDate(f.fecha_emision).includes(search) ||
      f.forma_pago?.toLowerCase().includes(searchLower);
    const matchEstado = !filterEstado || f.estado === filterEstado;
    const matchTipo = !filterTipo || f.tipo === filterTipo;
    return matchSearch && matchEstado && matchTipo;
  });

  const getEstadoBadge = (estado) => {
    const est = ESTADOS_FACTURA.find(e => e.value === estado);
    return est || ESTADOS_FACTURA[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6" /> Contabilidad
          </h1>
          <p className="text-gray-600">Gestión de facturas, presupuestos y pagos</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva factura
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total facturado</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_pagado + stats.total_pendiente)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cobrado</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_pagado)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente</p>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.total_pendiente)}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Facturas vencidas</p>
                <p className="text-2xl font-bold text-red-600">{stats.facturas_vencidas || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="facturas" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Facturas
          </TabsTrigger>
          <TabsTrigger value="gastos" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Controles de Gastos
          </TabsTrigger>
          <TabsTrigger value="morosidad" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Morosidad
          </TabsTrigger>
          <TabsTrigger value="presupuestos" className="flex items-center gap-2">
            <PiggyBank className="w-4 h-4" /> Presupuestos
          </TabsTrigger>
          <TabsTrigger value="informes" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Informes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="facturas" className="mt-4">
          {/* Filtros */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por número, concepto o cliente..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {ESTADOS_FACTURA.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterTipo || 'all'} onValueChange={(v) => setFilterTipo(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {TIPOS_FACTURA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tabla */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredFacturas.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          No se encontraron facturas
                        </td>
                      </tr>
                    ) : (
                      filteredFacturas.map(factura => {
                        const estadoBadge = getEstadoBadge(factura.estado);
                        const EstadoIcon = estadoBadge.icon;
                        return (
                          <tr 
                            key={factura.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/contabilidad/factura/${factura.id}`)}
                          >
                            <td className="px-4 py-3">
                              <span className="font-medium text-blue-600">{factura.numero_factura || `FAC-${factura.id}`}</span>
                              <div className="text-xs text-gray-500 capitalize">{factura.tipo}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{factura.cliente_nombre || '-'}</div>
                              {factura.codigo_expediente && (
                                <div className="text-xs text-gray-500">Exp: {factura.codigo_expediente}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 max-w-[200px] truncate">{factura.descripcion || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div>{formatDate(factura.fecha_emision)}</div>
                              {factura.fecha_vencimiento && (
                                <div className="text-xs text-gray-400">Vence: {formatDate(factura.fecha_vencimiento)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(factura.total)}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={estadoBadge.color}>
                                <EstadoIcon className="w-3 h-3 mr-1" />
                                {estadoBadge.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/contabilidad/factura/${factura.id}`)}
                                  title="Ver detalle"
                                >
                                  <Eye className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(factura)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(factura.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="gastos" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Gestión de gastos del despacho. Registra las facturas de proveedores.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contabilidad/gastos/estadisticas')}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Ver Estadísticas
                </Button>
                <Button onClick={() => navigate('/contabilidad/gastos')}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Ir a Controles de Gastos
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="py-12 text-center">
                <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Controles de Gastos</h3>
                <p className="text-gray-500 mt-2">
                  Lleva el control de las facturas de proveedores y gastos del despacho.
                </p>
                <p className="text-gray-500 mt-1">
                  Exporta datos, genera informes y visualiza estadísticas.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="morosidad" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Control de clientes morosos e impagos de facturas.
              </p>
              <Button onClick={() => navigate('/contabilidad/morosidad')}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Ir a Control de Morosidad
              </Button>
            </div>
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Control de Morosidad</h3>
                <p className="text-gray-500 mt-2">
                  Gestiona clientes con impagos, registra actuaciones y seguimiento de cobros.
                </p>
                <p className="text-gray-500 mt-1">
                  Incluye gestión de demandas judiciales y archivos adjuntos.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="presupuestos" className="mt-4">
          <Card>
            <CardContent className="py-12 text-center">
              <PiggyBank className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Gestión de Presupuestos</h3>
              <p className="text-gray-500 mt-2">Crea presupuestos y conviértelos en facturas cuando sean aceptados</p>
              <Button 
                className="mt-4"
                onClick={() => { resetForm(); setForm(prev => ({...prev, tipo: 'presupuesto'})); setIsDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="informes" className="mt-4">
          <InformesContabilidad facturas={facturas} contactos={contactos} />
        </TabsContent>
      </Tabs>

      {/* Dialog crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFactura ? 'Editar' : 'Nueva'} {form.tipo}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_FACTURA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número</Label>
                <Input 
                  value={form.numero} 
                  onChange={(e) => setForm({...form, numero: e.target.value})}
                  placeholder="Auto-generado"
                />
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({...form, estado: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS_FACTURA.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cliente</Label>
                <Select value={form.cliente_id} onValueChange={(v) => setForm({...form, cliente_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                  <SelectContent>
                    {contactos.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expediente (opcional)</Label>
                <Select value={form.expediente_id || 'none'} onValueChange={(v) => setForm({...form, expediente_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar expediente" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {expedientes.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.numero_expediente} - {e.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Concepto *</Label>
              <Textarea 
                value={form.concepto}
                onChange={(e) => setForm({...form, concepto: e.target.value})}
                placeholder="Descripción del servicio..."
                required
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Base imponible (€) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={form.base_imponible}
                  onChange={(e) => setForm({...form, base_imponible: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>IVA (%)</Label>
                <Input 
                  type="number"
                  value={form.iva}
                  onChange={(e) => setForm({...form, iva: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Total (€)</Label>
                <Input 
                  type="number"
                  value={form.total}
                  readOnly
                  className="bg-gray-50 font-bold text-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha emisión</Label>
                <Input 
                  type="date"
                  value={form.fecha_emision}
                  onChange={(e) => setForm({...form, fecha_emision: e.target.value})}
                />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input 
                  type="date"
                  value={form.fecha_vencimiento}
                  onChange={(e) => setForm({...form, fecha_vencimiento: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea 
                value={form.observaciones}
                onChange={(e) => setForm({...form, observaciones: e.target.value})}
                placeholder="Notas adicionales..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Constantes para gráficos
const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const MESES_CHART = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Componente de Informes de Contabilidad con gráficos
const InformesContabilidad = ({ facturas = [], contactos = [] }) => {
  
  // Asegurar que son arrays
  const facturasArray = Array.isArray(facturas) ? facturas : [];
  const contactosArray = Array.isArray(contactos) ? contactos : [];
  
  // Calcular facturación por mes
  const facturacionPorMes = useMemo(() => {
    const datos = {};
    const añoActual = new Date().getFullYear();
    
    // Inicializar todos los meses
    MESES_CHART.forEach((mes, i) => {
      datos[i] = { mes, emitidas: 0, cobradas: 0, pendiente: 0 };
    });
    
    facturasArray.forEach(f => {
      if (!f.fecha_emision) return;
      const fecha = new Date(f.fecha_emision);
      if (fecha.getFullYear() !== añoActual) return;
      
      const mesIdx = fecha.getMonth();
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      
      if (f.tipo === 'emitida' || f.tipo === 'factura') {
        datos[mesIdx].emitidas += total;
        datos[mesIdx].cobradas += pagado;
        datos[mesIdx].pendiente += (total - pagado);
      }
    });
    
    return Object.values(datos);
  }, [facturasArray]);
  
  // Calcular top clientes por facturación
  const topClientes = useMemo(() => {
    const clienteMap = {};
    
    facturasArray.forEach(f => {
      if (!f.cliente_id || f.estado === 'anulada') return;
      const total = parseFloat(f.total) || 0;
      
      if (!clienteMap[f.cliente_id]) {
        const cliente = contactosArray.find(c => c.id === f.cliente_id);
        clienteMap[f.cliente_id] = {
          id: f.cliente_id,
          nombre: cliente?.nombre || f.cliente_nombre || 'Sin nombre',
          total: 0,
          facturas: 0
        };
      }
      
      clienteMap[f.cliente_id].total += total;
      clienteMap[f.cliente_id].facturas += 1;
    });
    
    return Object.values(clienteMap)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [facturas, contactos]);
  
  // Calcular distribución por estado
  const distribucionEstados = useMemo(() => {
    const estados = { pendiente: 0, parcial: 0, pagada: 0, vencida: 0, anulada: 0 };
    
    facturasArray.forEach(f => {
      const estado = f.estado || 'pendiente';
      if (estados[estado] !== undefined) {
        estados[estado] += 1;
      }
    });
    
    return Object.entries(estados)
      .filter(([_, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
  }, [facturas]);
  
  // Calcular resumen financiero
  const resumen = useMemo(() => {
    let totalEmitido = 0;
    let totalCobrado = 0;
    let totalPendiente = 0;
    let facturasVencidas = 0;
    
    const hoy = new Date();
    
    facturasArray.forEach(f => {
      if (f.estado === 'anulada') return;
      
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      
      if (f.tipo === 'emitida' || f.tipo === 'factura') {
        totalEmitido += total;
        totalCobrado += pagado;
        totalPendiente += (total - pagado);
        
        if (f.fecha_vencimiento && new Date(f.fecha_vencimiento) < hoy && pagado < total) {
          facturasVencidas++;
        }
      }
    });
    
    return { totalEmitido, totalCobrado, totalPendiente, facturasVencidas };
  }, [facturas]);
  
  // Evolución mensual (últimos 12 meses)
  const evolucionMensual = useMemo(() => {
    const datos = [];
    const hoy = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const mes = MESES_CHART[fecha.getMonth()];
      const año = fecha.getFullYear();
      
      let emitido = 0;
      let cobrado = 0;
      
      facturasArray.forEach(f => {
        if (!f.fecha_emision) return;
        const fFecha = new Date(f.fecha_emision);
        if (fFecha.getMonth() === fecha.getMonth() && fFecha.getFullYear() === fecha.getFullYear()) {
          if (f.tipo === 'emitida' || f.tipo === 'factura') {
            emitido += parseFloat(f.total) || 0;
            cobrado += parseFloat(f.total_pagado) || 0;
          }
        }
      });
      
      datos.push({ mes: `${mes} ${año.toString().slice(2)}`, emitido, cobrado });
    }
    
    return datos;
  }, [facturas]);
  
  return (
    <div className="space-y-6">
      {/* Resumen financiero */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Emitido</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(resumen.totalEmitido)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Cobrado</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(resumen.totalCobrado)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente Cobro</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(resumen.totalPendiente)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Facturas Vencidas</p>
                <p className="text-2xl font-bold text-red-600">{resumen.facturasVencidas}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolución mensual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Evolución Mensual (Últimos 12 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolucionMensual}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="emitido" name="Emitido" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cobrado" name="Cobrado" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Facturación del año actual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Facturación {new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={facturacionPorMes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="emitidas" name="Emitido" fill="#3B82F6" />
                  <Bar dataKey="cobradas" name="Cobrado" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Segunda fila de gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribución por estado */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estado de Facturas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribucionEstados}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {distribucionEstados.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Top 10 Clientes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Top 10 Clientes por Facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topClientes.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-400">
                No hay datos de facturación
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {topClientes.map((cliente, i) => (
                  <div key={cliente.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <span className={`text-lg font-bold ${i < 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                        #{i + 1}
                      </span>
                      <div>
                        <span className="font-medium">{cliente.nombre}</span>
                        <span className="text-xs text-gray-500 ml-2">({cliente.facturas} facturas)</span>
                      </div>
                    </div>
                    <span className="text-green-600 font-semibold">{formatCurrency(cliente.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contabilidad;
